import React, { useState } from 'react';
import { Layout } from '../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { Sun, Moon, User, Bell, Shield, Palette, LogOut, KeyRound, Loader2 } from 'lucide-react';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

export default function Settings() {
  const { user, theme, toggleTheme, logout } = useAuth();
  const [pwDialogOpen, setPwDialogOpen] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [changingPw, setChangingPw] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) { toast.error('Passwords do not match'); return; }
    if (pwForm.new_password.length < 6) { toast.error('Min 6 characters'); return; }
    setChangingPw(true);
    try {
      await api.post('/auth/change-password', { current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed successfully');
      setPwDialogOpen(false);
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed');
    } finally {
      setChangingPw(false);
    }
  };

  return (
    <Layout title="Settings">
      <div className="space-y-6 max-w-2xl" data-testid="settings-page">
        <div><h2 className="font-heading text-2xl font-bold">Settings</h2><p className="text-muted-foreground">Manage your account and application settings</p></div>

        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><User className="h-5 w-5" />Profile</CardTitle><CardDescription>Your account information</CardDescription></CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center"><span className="text-2xl font-heading font-bold text-primary">{user?.full_name?.charAt(0)?.toUpperCase() || 'U'}</span></div>
              <div><p className="font-heading font-semibold text-lg">{user?.full_name}</p><p className="text-muted-foreground">{user?.email}</p>
                <Badge variant={user?.role === 'admin' ? 'default' : 'secondary'} className={`mt-1 ${user?.role === 'admin' ? 'bg-amber-500 text-white' : ''}`}>{user?.role === 'admin' ? 'Admin' : 'User'}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Palette className="h-5 w-5" />Appearance</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {theme === 'dark' ? <Moon className="h-5 w-5 text-primary" /> : <Sun className="h-5 w-5 text-amber-500" />}
                <div><Label className="font-medium">Dark Theme</Label><p className="text-sm text-muted-foreground">{theme === 'dark' ? 'Dark theme active' : 'Light theme active'}</p></div>
              </div>
              <Switch checked={theme === 'dark'} onCheckedChange={toggleTheme} />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Shield className="h-5 w-5" />Security</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <KeyRound className="h-5 w-5 text-muted-foreground" />
                <div><Label className="font-medium">Change Password</Label><p className="text-sm text-muted-foreground">Update your account password</p></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setPwDialogOpen(true)}>Change</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="font-heading flex items-center gap-2"><Bell className="h-5 w-5" />Notifications</CardTitle></CardHeader>
          <CardContent>
            <div className="flex items-center justify-between"><div><Label className="font-medium">Email Notifications</Label><p className="text-sm text-muted-foreground">Payment reminders</p></div><Badge variant="outline">Coming Soon</Badge></div>
          </CardContent>
        </Card>

        <Card className="border-destructive/50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div><p className="font-medium text-destructive">Sign Out</p><p className="text-sm text-muted-foreground">Securely sign out</p></div>
              <Button variant="destructive" onClick={logout}><LogOut className="h-4 w-4 mr-2" />Sign Out</Button>
            </div>
          </CardContent>
        </Card>
      </div>

      <Dialog open={pwDialogOpen} onOpenChange={setPwDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Password</DialogTitle><DialogDescription>Enter current and new password</DialogDescription></DialogHeader>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-2"><Label>Current Password</Label><Input type="password" value={pwForm.current_password} onChange={e => setPwForm({ ...pwForm, current_password: e.target.value })} required autoFocus /></div>
            <div className="space-y-2"><Label>New Password</Label><Input type="password" value={pwForm.new_password} onChange={e => setPwForm({ ...pwForm, new_password: e.target.value })} placeholder="Min 6 characters" required /></div>
            <div className="space-y-2"><Label>Confirm New Password</Label><Input type="password" value={pwForm.confirm_password} onChange={e => setPwForm({ ...pwForm, confirm_password: e.target.value })} required /></div>
            <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setPwDialogOpen(false)}>Cancel</Button><Button type="submit" disabled={changingPw}>{changingPw && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Change Password</Button></div>
          </form>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
